<template>
  <!-- Teleported to body (replaces the source's createPortal into the
       PhoneFrame overlay root) so the sheet always sits above the pass
       content regardless of scroll. `theme` (--fr-* vars) must be bound
       here because CSS custom properties don't cascade through Teleport. -->
  <Teleport to="body">
    <div v-if="open && place" class="pds-overlay" data-overlay :style="theme">
      <button
        id="place-detail-backdrop"
        type="button"
        aria-label="Close"
        class="pds-backdrop gtm-click"
        @click="emit('close')"
      />
      <div class="pds-sheet">
        <!-- Featured ribbon — same top-of-image banner as the featured-business
             card (left = period kicker, right = label). Renders above the hero
             for both the image and no-image variants so the top corners round. -->
        <div v-if="place.featured" class="pds-ribbon">
          <span class="pds-ribbon__left"><Sparkles :size="12" />{{ place.featured.kicker }}</span>
          <span v-if="place.featured.label" class="pds-ribbon__right">{{ place.featured.label }}</span>
        </div>

        <!-- Hero -->
        <div class="pds-hero" :class="{ 'pds-hero--ribboned': place.featured }">
          <div v-if="place.img" class="pds-hero__media">
            <img :src="place.img" :alt="place.name" class="pds-hero__img" />
            <div class="pds-hero__scrim" />
          </div>
          <div v-else class="pds-hero__bar" :style="{ background: accent }" />
          <button
            id="place-detail-close"
            type="button"
            aria-label="Close"
            class="pds-close gtm-click"
            @click="emit('close')"
          >
            <X :size="16" />
          </button>
          <div class="pds-handle" />
          <div v-if="place.img" class="pds-hero__text">
            <div v-if="place.kinds && place.kinds.length" class="pds-kinds">
              <span v-for="k in place.kinds" :key="k" class="pds-kind pds-kind--on-image">{{ k }}</span>
            </div>
            <h2 class="pds-title pds-title--on-image">{{ place.name }}</h2>
            <div v-if="place.hoursString" class="pds-hero__hours">
              <OpenStatus :weekly="place.hoursWeekly" :hours-string="place.hoursString" :timezone="timezone" />
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="pds-body no-scrollbar" data-scrollable>
          <div v-if="!place.img">
            <div v-if="place.kinds && place.kinds.length" class="pds-kinds">
              <span v-for="k in place.kinds" :key="k" class="pds-kind pds-kind--plain">{{ k }}</span>
            </div>
            <h2 class="pds-title">{{ place.name }}</h2>
            <div v-if="place.hoursString" class="pds-plain-hours">
              <OpenStatus :weekly="place.hoursWeekly" :hours-string="place.hoursString" :timezone="timezone" />
            </div>
          </div>

          <!-- Coupon (if present) — up top so it stands out -->
          <!-- Coupons — venues can carry more than one; one card per coupon.
               Accepts `coupons: []` (preferred) or the legacy single `coupon`. -->
          <div
            v-for="(c, ci) in placeCoupons"
            :key="`coupon-${ci}`"
            :id="`place-detail-redeem-coupon-${ci}`"
            class="pds-coupon"
            :class="{ 'pds-coupon--tappable gtm-click': c.state === 'history' || c.state === 'unavailable' }"
            :role="c.state === 'history' || c.state === 'unavailable' ? 'button' : null"
            :tabindex="c.state === 'history' || c.state === 'unavailable' ? 0 : null"
            :style="{ background: accentWash }"
            @click="(c.state === 'history' || c.state === 'unavailable') && onRedeemCoupon(c)"
            @keydown.enter="(c.state === 'history' || c.state === 'unavailable') && onRedeemCoupon(c)"
          >
            <div class="pds-coupon__row">
              <div class="pds-coupon__icon" :style="{ background: accent }">
                <Ticket :size="16" />
              </div>
              <div class="pds-coupon__text">
                <div v-if="c.state === 'redeem' || c.state === 'view'" class="pds-coupon__eyebrow" :style="{ color: accent }">
                  Coupon{{ placeCoupons.length > 1 ? ` ${ci + 1} of ${placeCoupons.length}` : '' }} available
                </div>
                <div v-else-if="c.state === 'unavailable'" class="pds-coupon__eyebrow pds-coupon__eyebrow--muted">
                  Unavailable
                </div>
                <div class="pds-coupon__headline">{{ c.headline }}</div>
                <div v-if="c.sub" class="pds-coupon__sub">{{ c.sub }}</div>
                <div v-if="c.expires" class="pds-coupon__expires">
                  Ends {{ c.expires }}
                </div>
              </div>
              <ChevronRight v-if="c.state === 'history' || c.state === 'unavailable'" :size="18" class="pds-coupon__chev" :style="{ color: accent }" />
            </div>
            <button
              v-if="c.state === 'redeem' || c.state === 'view'"
              type="button"
              class="pds-coupon__cta gtm-click"
              :style="{ background: accent }"
              @click="onRedeemCoupon(c)"
            >
              {{ c.state === 'view' ? 'View coupon' : 'Redeem coupon' }}
            </button>
          </div>

          <!-- Description -->
          <p v-if="place.description" class="pds-description" v-html="place.description"></p>

          <!-- Media -->
          <div v-if="place.audioUrl" class="pds-audio">
            <div class="pds-media-head">
              <Volume2 :size="16" :style="{ color: accent }" />
              <div class="pds-media-label">{{ place.audioLabel ?? 'Audio tour' }}</div>
            </div>
            <audio controls :src="place.audioUrl" class="pds-audio__player" />
          </div>
          <div v-if="place.videoUrl" class="pds-video">
            <div class="pds-video__head">
              <PlayCircle :size="16" :style="{ color: accent }" />
              <div class="pds-media-label">Video</div>
            </div>
            <video
              controls
              :src="place.videoUrl"
              :poster="place.videoPoster"
              class="pds-video__player"
            />
          </div>

          <!-- Hours grid -->
          <div v-if="place.weeklyHours && place.weeklyHours.length">
            <div class="pds-info-label">
              <Clock :size="16" :style="{ color: accent }" />
              Hours
            </div>
            <div class="pds-hours-grid">
              <template v-for="r in place.weeklyHours" :key="r.day">
                <div class="pds-hours-grid__day">{{ r.day }}</div>
                <div>{{ r.hours }}</div>
              </template>
            </div>
          </div>

          <!-- Contact rows -->
          <div class="pds-rows">
            <a
              v-if="place.address"
              id="place-detail-directions"
              class="pds-row gtm-click"
              :href="mapsUrl(place)"
              target="_blank"
              rel="noopener"
            >
              <span class="pds-row__icon"><Navigation :size="16" :style="{ color: accent }" /></span>
              <span class="pds-row__label">{{ place.address }}</span>
              <span class="pds-row__cta" :style="{ background: accent }">Directions</span>
            </a>
            <a
              v-if="place.phone"
              id="place-detail-call"
              class="pds-row gtm-click"
              :href="`tel:${place.phone.replace(/[^\d+]/g, '')}`"
            >
              <span class="pds-row__icon"><Phone :size="16" :style="{ color: accent }" /></span>
              <span class="pds-row__label">{{ place.phone }}</span>
              <span class="pds-row__cta" :style="{ background: accent }">Call</span>
            </a>
            <a
              v-if="place.website"
              id="place-detail-website"
              class="pds-row gtm-click"
              :href="place.website"
              :target="place.website.startsWith('http') ? '_blank' : undefined"
              rel="noreferrer"
            >
              <span class="pds-row__icon"><Globe :size="16" :style="{ color: accent }" /></span>
              <span class="pds-row__label">{{ place.website.replace(/^https?:\/\//, '') }}</span>
              <span class="pds-row__cta" :style="{ background: accent }">Visit</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { X, Clock, Phone, Globe, Navigation, PlayCircle, Volume2, Ticket, Sparkles, ChevronRight } from '@lucide/vue'
import { mapsUrl } from '@/helpers'
import OpenStatus from './OpenStatus.vue'

/**
 * PlaceDetailSheet — universal "Place Details" page.
 *
 * Full-height bottom sheet, reusable across pass layouts. Any layout can
 * mount it by passing a `place` payload; audio/video/coupon are optional
 * and only render when supplied.
 *
 * Pixel-perfect port of the Lovable source `_shared/PlaceDetailsSheet.tsx`.
 *
 * place = {
 *   id, name, img?, kinds?: string[], featured?: { kicker, label? }, description?, hoursString?,
 *   weeklyHours?: [{ day, hours }], phone?, website?, address?,
 *   audioUrl?, audioLabel?, videoUrl?, videoPoster?,
 *   coupon?: { headline, sub?, expires?, onRedeem() }
 * }
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  place: { type: Object, default: null },
  /** Style object of --fr-* CSS vars (e.g. useFrTokens().value) — required on
      teleported overlays because vars don't cascade through Teleport. */
  theme: { type: Object, default: null },
  /** Optional accent color (e.g. brand orange). Falls back to --fr-primary. */
  accentColor: { type: String, default: null },
  timezone: { type: String, default: null }
})

const emit = defineEmits(['close', 'redeem-coupon'])

// Venues can carry more than one coupon; normalize array/legacy-single shapes.
const placeCoupons = computed(() => {
  const p = props.place
  if (!p) return []
  if (Array.isArray(p.coupons)) return p.coupons
  return p.coupon ? [p.coupon] : []
})

function onRedeemCoupon(c) {
  if (c.onRedeem) c.onRedeem()
  emit('redeem-coupon', c)
}

const accent = computed(() => props.accentColor ?? 'var(--fr-primary, #FF9900)')
// Source: `${accent}12` (hex alpha 0x12 ≈ 7%). color-mix keeps this working
// when accent is a CSS var() reference instead of a raw hex.
const accentWash = computed(() => `color-mix(in srgb, ${accent.value} 7%, transparent)`)
</script>

<style lang="scss" scoped>
button {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

a {
  text-decoration: none;
  color: inherit;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

@keyframes pds-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes pds-slide-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

// absolute inset-0 z-[155] flex flex-col animate-in fade-in duration-150
.pds-overlay {
  position: fixed;
  inset: 0;
  z-index: 2400; // above the pass layout + app chrome
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  animation: pds-fade-in 150ms ease both;
}

// absolute inset-0 bg-black/50
.pds-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

// relative mt-auto flex max-h-[95%] w-full flex-col rounded-t-3xl
// bg-surface shadow-2xl animate-in slide-in-from-bottom duration-300
.pds-sheet {
  position: relative;
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  max-height: 95%;
  width: 100%;
  // No PhoneFrame in v4 — constrain to the pass column instead.
  max-width: var(--project-max-width, 480px);
  flex-direction: column;
  border-radius: 24px 24px 0 0;
  background: var(--fr-surface, #ffffff);
  color: var(--fr-ink, #1c1c1e);
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  animation: pds-slide-up 300ms ease both;
}

// relative shrink-0 overflow-hidden rounded-t-3xl
.pds-hero {
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 24px 24px 0 0;

  // relative aspect-[16/9] w-full
  &__media {
    position: relative;
    aspect-ratio: 16 / 9;
    width: 100%;
  }

  // h-full w-full object-cover
  &__img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    display: block;
  }

  // absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent
  &__scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%);
  }

  // h-14 w-full (accent-colored bar when no image)
  &__bar {
    height: 56px;
    width: 100%;
  }

  // absolute inset-x-0 bottom-0 p-4 text-white
  &__text {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px;
    color: #ffffff;
  }

  // mt-1 flex items-center gap-2 text-[11px] text-white/85
  &__hours {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);
  }
}

// absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full
// bg-black/50 text-white backdrop-blur
.pds-close {
  position: absolute;
  right: 12px;
  top: 12px;
  display: grid;
  height: 36px;
  width: 36px;
  place-items: center;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

// mx-auto mt-2 h-1 w-10 rounded-full bg-white/60
.pds-handle {
  margin: 8px auto 0;
  height: 4px;
  width: 40px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.6);
}

// mb-1 flex flex-wrap gap-1
.pds-kinds {
  margin-bottom: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

// Featured ribbon — same top-of-image banner as the featured-business card
// (index.vue .dg-featured__ribbon): full-width orange gradient bar with a
// period kicker on the left and a label on the right.
.pds-ribbon {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  color: #ffffff;
  background: linear-gradient(90deg, var(--fr-primary, #FF9900) 0%, var(--fr-primary-light, #FFB84D) 50%, var(--fr-primary, #FF9900) 100%);
  border-radius: 24px 24px 0 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  // Optional right-aligned label (only when a layout passes `label`).
  &__right {
    margin-left: auto;
  }
}
// When the ribbon is present it owns the top rounding; square off the hero.
.pds-hero--ribboned {
  border-radius: 0;
}

// rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider
.pds-kind {
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  // bg-white/20 backdrop-blur (over the hero image)
  &--on-image {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

  // bg-current/10 (no-image header)
  &--plain {
    background: color-mix(in srgb, currentColor 10%, transparent);
  }
}

// font-serif-display text-xl font-black italic leading-tight
.pds-title {
  margin: 0;
  font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
  font-size: 20px;
  font-weight: 900;
  font-style: italic;
  line-height: 1.25;

  // drop-shadow
  &--on-image {
    filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.1)) drop-shadow(0 1px 1px rgb(0 0 0 / 0.06));
  }
}

// mt-1 (OpenStatus under the plain title)
.pds-plain-hours {
  margin-top: 4px;
}

// min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4
.pds-body {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;

  > * + * {
    margin-top: 16px;
  }
}

// rounded-2xl p-3 ring-1 ring-current/10 (bg = accent wash, inline)
.pds-coupon {
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 0 0 1px color-mix(in srgb, currentColor 10%, transparent);

  &--tappable { cursor: pointer; }

  // flex items-center gap-2.5
  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__chev {
    align-self: center;
    flex-shrink: 0;
  }

  // grid h-9 w-9 shrink-0 place-items-center rounded-full text-white
  &__icon {
    display: grid;
    height: 36px;
    width: 36px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 9999px;
    color: #ffffff;
  }

  // min-w-0 flex-1
  &__text {
    min-width: 0;
    flex: 1;
  }

  // font-display text-[9px] font-bold uppercase tracking-wider
  &__eyebrow {
    font-family: var(--fr-font-display, inherit);
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &--muted {
      color: color-mix(in srgb, currentColor 45%, transparent);
    }
  }

  // font-serif-display text-sm font-black leading-tight
  &__headline {
    font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
    font-size: 14px;
    font-weight: 900;
    line-height: 1.25;
  }

  // text-[11px] muted
  &__sub {
    font-size: 11px;
    color: var(--fr-muted, #6b7280);
  }

  // mt-0.5 text-[10px] uppercase tracking-wider muted
  &__expires {
    margin-top: 2px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--fr-muted, #6b7280);
  }

  // mt-2 w-full rounded-full py-2 font-display text-[11px] font-bold
  // uppercase tracking-wider text-white
  &__cta {
    margin-top: 8px;
    width: 100%;
    border-radius: 9999px;
    padding: 8px 0;
    font-family: var(--fr-font-display, inherit);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #ffffff;
  }
}

// text-[13px] leading-relaxed
.pds-description {
  margin: 0;
  font-size: 13px;
  line-height: 1.625;
}
.pds-description :deep(ul) { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
.pds-description :deep(ol) { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
.pds-description :deep(li) { margin: 0.25rem 0; }
// The `margin: 0` above (needed to drop the <p>'s UA margins) also defeats the
// `.pds-body > * + *` owl spacing at equal specificity + later source order, so
// the paragraph hugged the coupon card above it (0px gap). Restore the 16px gap
// with a higher-specificity sibling rule; first-child description is unaffected.
.pds-body > * + .pds-description {
  margin-top: 16px;
}

// rounded-xl bg-current/5 p-3 ring-1 ring-current/10
.pds-audio {
  border-radius: 12px;
  background: color-mix(in srgb, currentColor 5%, transparent);
  padding: 12px;
  box-shadow: 0 0 0 1px color-mix(in srgb, currentColor 10%, transparent);

  // w-full
  &__player {
    width: 100%;
    display: block;
  }
}

// mb-2 flex items-center gap-2
.pds-media-head {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

// font-display text-[10px] font-bold uppercase tracking-wider
.pds-media-label {
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

// overflow-hidden rounded-xl ring-1 ring-current/10
.pds-video {
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 0 0 1px color-mix(in srgb, currentColor 10%, transparent);

  // flex items-center gap-2 bg-current/5 px-3 py-2
  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    background: color-mix(in srgb, currentColor 5%, transparent);
    padding: 8px 12px;
  }

  // w-full bg-black
  &__player {
    width: 100%;
    background: #000000;
    display: block;
  }
}

// mb-1.5 flex items-center gap-2 text-[10px] font-bold uppercase
// tracking-wider opacity-70
.pds-info-label {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}

// grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]
.pds-hours-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 12px;
  row-gap: 4px;
  font-size: 12px;

  // font-medium muted
  &__day {
    font-weight: 500;
    color: var(--fr-muted, #6b7280);
  }
}

// space-y-2
.pds-rows {
  > * + * {
    margin-top: 8px;
  }
}

// flex w-full items-center gap-2.5 rounded-xl bg-current/[0.03] p-2
// ring-1 ring-current/10 transition hover:bg-current/[0.06]
.pds-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  background: color-mix(in srgb, currentColor 3%, transparent);
  padding: 8px;
  box-shadow: 0 0 0 1px color-mix(in srgb, currentColor 10%, transparent);
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;

  &:hover {
    background: color-mix(in srgb, currentColor 6%, transparent);
  }

  // grid h-8 w-8 shrink-0 place-items-center rounded-full bg-current/5
  &__icon {
    display: grid;
    height: 32px;
    width: 32px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 9999px;
    background: color-mix(in srgb, currentColor 5%, transparent);
  }

  // flex-1 truncate text-[12px]
  &__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
  }

  // shrink-0 rounded-full px-2.5 py-1 font-display text-[10px] font-bold
  // uppercase tracking-wider text-white
  &__cta {
    flex-shrink: 0;
    border-radius: 9999px;
    padding: 4px 10px;
    font-family: var(--fr-font-display, inherit);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #ffffff;
  }
}
</style>
